/**
 * Types for SEO audit detail components
 */

// SE Ranking section prop from API
export interface SerankingSectionProp {
  code: string
  status: 'error' | 'warning' | 'notice' | 'passed'
  name: string
  value: number
}

// SE Ranking section from API
export interface SerankingSection {
  uid: string
  name: string
  props: Record<string, SerankingSectionProp>
}

// SE Ranking raw data structure
export interface SerankingRawData {
  total_pages?: number
  total_warnings?: number
  total_errors?: number
  total_passed?: number
  total_notices?: number
  sections?: SerankingSection[]
}

// Parsed issue for display
export interface SeoIssue {
  code: string
  name: string
  status: 'error' | 'warning' | 'notice' | 'passed'
  value: number
  sectionUid: string
  sectionName: string
}

// Parsed issues grouped by status and section
export interface ParsedIssues {
  byStatus: {
    error: SeoIssue[]
    warning: SeoIssue[]
    notice: SeoIssue[]
    passed: SeoIssue[]
  }
  bySection: Record<string, {
    name: string
    issues: SeoIssue[]
    passedCount: number
  }>
}

// PageSpeed audit opportunity
export interface PageSpeedOpportunity {
  id: string
  title: string
  description?: string
  score: number | null
  numericValue?: number
  numericUnit?: string
  displayValue?: string
  savings?: string // formatted savings (e.g., "1.2s", "250 KB")
}

// PageSpeed raw data structure (simplified)
export interface PageSpeedRawData {
  mobile?: {
    rawData?: {
      lighthouseResult?: {
        audits?: Record<string, {
          id: string
          title: string
          description?: string
          score: number | null
          numericValue?: number
          numericUnit?: string
          displayValue?: string
          details?: {
            type?: string
            overallSavingsMs?: number
            overallSavingsBytes?: number
          }
        }>
      }
    }
  }
  desktop?: {
    rawData?: {
      lighthouseResult?: {
        audits?: Record<string, {
          id: string
          title: string
          description?: string
          score: number | null
          numericValue?: number
          numericUnit?: string
          displayValue?: string
          details?: {
            type?: string
            overallSavingsMs?: number
            overallSavingsBytes?: number
          }
        }>
      }
    }
  }
}

// Page-level issue data from audit
export interface AuditPage {
  id: number
  url: string
  httpCode: number | null
  issues: string[]
  title: string | null
  loadTime: number | null
}

// Response from /api/seo/audit/:domain/pages
export interface AuditPagesResponse {
  pages: AuditPage[]
  audit: {
    id: number
    date: string
    healthScore: number | null
    pagesCrawled: number | null
  } | null
}

// Section name mapping for better display
export const SECTION_NAMES: Record<string, string> = {
  security_v2: 'Security',
  crawling_v2: 'Crawling & Indexing',
  metatags_v2: 'Meta Tags',
  content_v2: 'Content',
  links_v2: 'Links',
  images_v2: 'Images',
  performance_v2: 'Performance',
  mobile_v2: 'Mobile',
  social_v2: 'Social Media',
  international_v2: 'International',
  schema_v2: 'Schema Markup',
  other_v2: 'Other',
}
